import { useNotifications } from "../hooks/useNotifications";

export type Tab = "today" | "lists" | "meals" | "notes" | "family";

interface TabBarProps {
  active: Tab;
  onChange: (tab: Tab) => void;
}

const FamilyIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="currentColor" aria-hidden="true">
    <path d="M48-144v-168q0-30 21-51t51-21h133q18 0 33 8.5t26 22.5q29 42 73 65.5t95 23.5q51 0 95-23.5t73-65.5q11-14 26-22.5t33-8.5h133q30 0 51 21t21 51v168H624v-90q-31 23-68.5 32.5T480-192q-38 0-75-9.5T336-234v90H48Zm432-156q-42 0-78-19.5T342-373q-14-19-33-31t-42-14q47-32 101.5-47T480-480q57 0 111.5 15T693-418q-23 2-42 14t-33 31q-24 34-60 53.5T480-300ZM168-432q-50 0-85-35t-35-85q0-50 35-85t85-35q50 0 85 35t35 85q0 50-35 85t-85 35Zm624 0q-50 0-85-35t-35-85q0-50 35-85t85-35q50 0 85 35t35 85q0 50-35 85t-85 35Zm-312-96q-50 0-85-35t-35-85q0-50 35-85t85-35q50 0 85 35t35 85q0 50-35 85t-85 35Z"/>
  </svg>
);

const ListsIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="currentColor" aria-hidden="true">
    <path d="M216-216v-528 444-85 169Zm0 72q-29.7 0-50.85-21.15Q144-186.3 144-216v-528q0-29.7 21.15-50.85Q186.3-816 216-816h528q29.7 0 50.85 21.15Q816-773.7 816-744v312h-72v-312H216v528h264v72H216Zm475 48L556-232l51-51 84 85 170-170 51 51L691-96ZM349.5-454.29q10.5-10.29 10.5-25.5t-10.29-25.71q-10.29-10.5-25.5-10.5t-25.71 10.29q-10.5 10.29-10.5 25.5t10.29 25.71q10.29 10.5 25.5 10.5t25.71-10.29Zm0-156q10.5-10.29 10.5-25.5t-10.29-25.71q-10.29-10.5-25.5-10.5t-25.71 10.29q-10.5 10.29-10.5 25.5t10.29 25.71q10.29 10.5 25.5 10.5t25.71-10.29ZM432-444h240v-72H432v72Zm0-156h240v-72H432v72Z"/>
  </svg>
);

const MealsIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="currentColor" aria-hidden="true">
    <path d="M300-96v-389q-47-12-77.5-50.1T192-624v-240h72v240h36v-240h72v240h36v-240h72v240q0 50.8-30.5 88.9Q419-497 372-485v389h-72Zm396 0v-336H576v-240q0-79.68 56.16-135.84T768-864v768h-72Z"/>
  </svg>
);

const NotesIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="currentColor" aria-hidden="true">
    <path d="M216-144q-29.7 0-50.85-21.15Q144-186.3 144-216v-528q0-29.7 21.15-50.85Q186.3-816 216-816h171q8-31 33.5-51.5T480-888q34 0 59.5 20.5T573-816h171q29.7 0 50.85 21.15Q816-773.7 816-744v528q0 29.7-21.15 50.85Q773.7-144 744-144H216Zm0-72h528v-528H216v528Zm72-72h288v-72H288v72Zm0-156h384v-72H288v72Zm0-156h384v-72H288v72Zm209-175q7-7 7-17t-7-17q-7-7-17-7t-17 7q-7 7-7 17t7 17q7 7 17 7t17-7ZM216-216v-528 528Z"/>
  </svg>
);

const tabs: { id: Tab; label: string; icon?: string; svgIcon?: React.FC }[] = [
  { id: "today", label: "Today", icon: "home" },
  { id: "lists", label: "Lists", svgIcon: ListsIcon },
  { id: "meals", label: "Meals", svgIcon: MealsIcon },
  { id: "notes", label: "Notes", svgIcon: NotesIcon },
  { id: "family", label: "Family", svgIcon: FamilyIcon },
];

export function TabBar({ active, onChange }: TabBarProps) {
  const { notifications } = useNotifications();
  const hasUnreadFamily = notifications.some((n) => n.channel === "family");

  return (
    <nav className="tab-bar" role="tablist">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          role="tab"
          aria-selected={active === tab.id}
          className={`tab-item ${active === tab.id ? "active" : ""}`}
          onClick={() => onChange(tab.id)}
        >
          {tab.svgIcon ? (
            <>
              <span style={{ position: "relative", display: "inline-flex" }}>
                <tab.svgIcon />
                {tab.id === "family" && hasUnreadFamily && (
                  <span
                    aria-label="Unread family messages"
                    style={{
                      position: "absolute",
                      bottom: "-2px",
                      right: "-4px",
                      width: "8px",
                      height: "8px",
                      borderRadius: "50%",
                      background: "#ff3b30",
                      border: "1.5px solid var(--tab-bar-bg, #fff)",
                    }}
                  />
                )}
              </span>
              <span className="tab-label">{tab.label}</span>
            </>
          ) : tab.icon ? (
            <span className="material-icons tab-icon">{tab.icon}</span>
          ) : (
            tab.label
          )}
        </button>
      ))}
    </nav>
  );
}
